import { useEffect, useRef, useState } from "react";
import {
  CameraDeviceInfo,
  Constants, MicrophoneDeviceInfo,
  useMediaDevice
} from "@videosdk.live/react-sdk";
import {
  Button, Col, Layout, Row, App, Modal, Flex, Typography,
} from "antd";

import { AudioOutlined, AudioMutedOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { Permission } from "@videosdk.live/react-sdk/dist/types/permission";
import { PlaybackDeviceInfo } from "@videosdk.live/react-sdk/dist/types/deviceInfo";
import { useTranslation } from "react-i18next";
import { MeetingDetailsScreen } from "../MeetingDetailsScreen";
import useMediaStream from "../../hooks/useMediaStream";
import useIsMobile from "../../hooks/useIsMobile";
import MicControls from "../MicControls";
import CamControls from "../CamControls";
import SpeakersControls from "../SpeakersControls/speakersControls";
import useVideoStore from "../../hooks/useVideoStore";

const { Content } = Layout;
const { Title } = Typography;

const JoiningScreen = (props: any) => {
  const { t } = useTranslation();
  const {
    customAudioStream,
    setCustomAudioStream,
    setCustomVideoStream
  } = props;

  const {
    webcamOn,
    micOn,
    setMicOn,
    setWebcamOn,
    selectedWebcam,
    selectedMic,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    isCameraPermissionAllowed,
    isMicrophonePermissionAllowed,
    setIsCameraPermissionAllowed,
    setIsMicrophonePermissionAllowed
  } = useVideoStore();

  const [{ webcams, mics, speakers }, setDevices] = useState<{
    webcams: CameraDeviceInfo[];
    mics: MicrophoneDeviceInfo[],
    speakers: PlaybackDeviceInfo[],
  }>({
    webcams: [],
    mics: [],
    speakers: [],
  });
  const { getVideoTrack, getAudioTrack } = useMediaStream();
  const [audioTrack, setAudioTrack] = useState<any | null>(null);
  const [videoTrack, setVideoTrack] = useState<any | null>(null);
  const [dlgMuted, setDlgMuted] = useState(false);
  const [dlgDevices, setDlgDevices] = useState(false);
  const [didDeviceChange, setDidDeviceChange] = useState(false);

  const videoPlayerRef = useRef<any | null>(null);
  const videoTrackRef = useRef<any | null>(null);
  const audioTrackRef = useRef<any | null>(null);
  const audioAnalyserIntervalRef = useRef<any | null>(null);
  const permissionAvailable = useRef<{
    isCameraPermissionAllowed:boolean,
    isMicrophonePermissionAllowed: boolean
  } | null>(null);
  const webcamRef = useRef<boolean | null>(null);
  const micRef = useRef<boolean | null>(null);
  const isMobile = useIsMobile();

  const { message } = App.useApp();
  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    if (dlgMuted) {
      modal.info({
        title: "System mic is muted",
        content: "You're default microphone is muted, please unmute it or increase audio input volume from system settings.",
        okText: "OKAY",
        onOk: () => { setDlgMuted(false); }
      });
    }
  }, [dlgMuted]);

  useEffect(() => {
    if (dlgDevices) {
      modal.info({
        title: "Mic or webcam not available",
        content: "Please connect a mic and webcam to speak and share your video in the meeting. You can also join without them.",
        okText: "DISMISS",
        onOk: () => { setDlgDevices(false); }
      });
    }
  }, [dlgDevices]);

  const getCameraDevices = async () => {
    try {
      if (permissionAvailable.current?.isCameraPermissionAllowed) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const webCameras = await getCameras();
        setSelectedWebcam({ id: webCameras[0]?.deviceId, label: webCameras[0]?.label });
        setDevices((devices) => {
          return { ...devices, webcams: webCameras };
        });
      }
    } catch (err) {
      message.error(`Error in getting camera devices, ${err}`);
    }
  };

  const getAudioDevices = async () => {
    try {
      if (permissionAvailable.current?.isMicrophonePermissionAllowed) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const microphones = await getMicrophones();
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const playback = await getPlaybackDevices();
        const hasMic = microphones.length > 0;
        if (hasMic) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          startMuteListener();
        }
        setSelectedSpeaker({ id: playback[0]?.deviceId, label: playback[0]?.label });
        setSelectedMic({ id: microphones[0]?.deviceId, label: microphones[0]?.label });
        setDevices((devices) => {
          return { ...devices, mics: microphones, speakers: playback };
        });
      }
    } catch (err) {
      message.error(`Error in getting audio devices, ${err}`);
    }
  };

  function onDeviceChanged() {
    setDidDeviceChange(true);
    getCameraDevices();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getAudioDevices();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getDefaultMediaTracks({ mic: !!micRef.current, webcam: !!webcamRef.current });
  }
  const {
    checkPermissions,
    getCameras,
    getMicrophones,
    requestPermission,
    getPlaybackDevices,
  } = useMediaDevice({ onDeviceChanged });

  useEffect(() => { webcamRef.current = webcamOn; }, [webcamOn]);

  useEffect(() => { micRef.current = micOn; }, [micOn]);

  useEffect(() => {
    permissionAvailable.current = {
      isCameraPermissionAllowed,
      isMicrophonePermissionAllowed,
    };
  }, [isCameraPermissionAllowed, isMicrophonePermissionAllowed]);

  useEffect(() => {
    if (micOn) {
      audioTrackRef.current = audioTrack;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      startMuteListener();
    }
  }, [micOn, audioTrack]);

  useEffect(() => {
    if (webcamOn) {
      videoTrackRef.current = videoTrack;

      const isPlaying = videoPlayerRef.current.currentTime > 0
        && !videoPlayerRef.current.paused
        && !videoPlayerRef.current.ended
        && videoPlayerRef.current.readyState
        > videoPlayerRef.current.HAVE_CURRENT_DATA;

      if (videoTrack) {
        const videoSrcObject = new MediaStream([videoTrack]);

        if (videoPlayerRef.current) {
          videoPlayerRef.current.srcObject = videoSrcObject;
          if (videoPlayerRef.current.pause && !isPlaying) {
            videoPlayerRef.current
              .play()
              .catch((error: string) => message.error(error));
          }
        }
      } else if (videoPlayerRef.current) {
        videoPlayerRef.current.srcObject = null;
      }
    } else {
      videoPlayerRef.current.srcObject = null;
      videoTrackRef.current = null;
    }
  }, [webcamOn, videoTrack]);

  useEffect(() => {
    getCameraDevices();
  }, [isCameraPermissionAllowed]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    getAudioDevices();
  }, [isMicrophonePermissionAllowed]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    checkMediaPermission();
  }, []);

  const toggleWebcam = () => {
    const track = videoTrackRef.current;

    if (webcamOn) {
      if (track) {
        track.stop();
        setVideoTrack(null);
        setCustomVideoStream(null);
        setWebcamOn(false);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      getDefaultMediaTracks({ mic: false, webcam: true });
      setWebcamOn(true);
    }
  };

  const toggleMic = () => {
    const track = audioTrackRef.current;

    if (micOn) {
      if (track) {
        track.stop();
        setAudioTrack(null);
        setCustomAudioStream(null);
        setMicOn(false);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      getDefaultMediaTracks({ mic: true, webcam: false });
      setMicOn(true);
    }
  };

  const changeWebcam = async (deviceId: string) => {
    if (webcamOn) {
      const currentvideoTrack = videoTrackRef.current;
      if (currentvideoTrack) {
        currentvideoTrack.stop();
      }

      const vStream = await getVideoTrack({
        webcamId: deviceId
      });
      setCustomVideoStream(vStream);
      const videoTracks = vStream?.getVideoTracks();
      const vTrack = videoTracks?.length ? videoTracks[0] : null;
      setVideoTrack(vTrack);
    }
  };
  const changeMic = async (deviceId: string) => {
    if (micOn) {
      const currentAudioTrack = audioTrackRef.current;
      if (currentAudioTrack) currentAudioTrack.stop();
      const aStream = await getAudioTrack({
        micId: deviceId
      });
      setCustomAudioStream(aStream);
      const audioTracks = aStream?.getAudioTracks() as MediaStreamTrack[] | undefined;
      const aTrack = audioTracks?.length ? audioTracks[0] : null;
      clearInterval(audioAnalyserIntervalRef.current);
      setAudioTrack(aTrack);
    }
  };

  const getDefaultMediaTracks = async (
    { mic, webcam }: { mic: boolean, webcam: boolean }
  ) => {
    if (mic) {
      const stream = await getAudioTrack({
        micId: selectedMic.id as string
      });
      setCustomAudioStream(stream);
      const audioTracks = stream?.getAudioTracks();
      const aTrack = audioTracks?.length ? audioTracks[0] : null;
      setAudioTrack(aTrack);
    }

    if (webcam) {
      const stream = await getVideoTrack({
        webcamId: selectedWebcam.id as string
      });
      setCustomVideoStream(stream);
      const videoTracks = stream?.getVideoTracks();
      const vTrack = videoTracks?.length ? videoTracks[0] : null;
      setVideoTrack(vTrack);
    }
  };

  async function startMuteListener() {
    const currentAudioTrack = audioTrackRef.current;
    if (currentAudioTrack) {
      if (currentAudioTrack.muted) {
        setDlgMuted(true);
      }
      currentAudioTrack.addEventListener("mute", () => {
        setDlgMuted(true);
      });
    }
  }

  async function requestAudioVideoPermission(mediaType: Permission) {
    try {
      const permission = await requestPermission(mediaType);

      if (mediaType === Constants.permission.AUDIO) {
        setIsMicrophonePermissionAllowed(
          !!permission.get(Constants.permission.AUDIO)
        );
      }

      if (mediaType === Constants.permission.VIDEO) {
        setIsCameraPermissionAllowed(
          !!permission.get(Constants.permission.VIDEO)
        );
      }

      if (permission.get(Constants.permission.AUDIO)) {
        setMicOn(true);
        getDefaultMediaTracks({ mic: true, webcam: false });
      }

      if (permission.get(Constants.permission.VIDEO)) {
        setWebcamOn(true);
        getDefaultMediaTracks({ mic: false, webcam: true });
      }
    } catch (ex) {
      message.error(`Error in requestPermission, ${ex}`);
    }
  }

  const checkMediaPermission = async () => {
    const checkAudioVideoPermission = await checkPermissions();
    const cameraPermissionAllowed = checkAudioVideoPermission.get(
      Constants.permission.VIDEO
    );
    const microphonePermissionAllowed = checkAudioVideoPermission.get(
      Constants.permission.AUDIO
    );

    setIsCameraPermissionAllowed(!!cameraPermissionAllowed);
    setIsMicrophonePermissionAllowed(!!microphonePermissionAllowed);

    if (microphonePermissionAllowed) {
      setMicOn(true);
      getDefaultMediaTracks({ mic: true, webcam: false });
    } else {
      await requestAudioVideoPermission(Constants.permission.AUDIO);
    }
    if (cameraPermissionAllowed) {
      setWebcamOn(true);
      getDefaultMediaTracks({ mic: false, webcam: true });
    } else {
      await requestAudioVideoPermission(Constants.permission.VIDEO);
    }
  };

  return (
    <>
      <Layout style={{ height: "100vh", background: "transparent" }}>
        <Content style={{
          maxWidth: "1200px", padding: "23vh 16px 0 ", width: "100%", margin: "0 auto"
        }}
        >
          <Row gutter={[32, 16]} style={{ height: "100%" }}>
            <Col
              span={24}
              lg={16}
            >
              <Flex
                vertical
                style={{
                  height: "56vh", width: "100%", position: "relative",
                }}
              >
                <div style={{ position: "relative", flexGrow: 1 }}>
                  <video
                    autoPlay
                    playsInline
                    muted
                    ref={videoPlayerRef}
                    controls={false}
                    style={{
                      backgroundColor: "#1c1c1c",
                      borderRadius: 10,
                      height: "100%",
                      width: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0
                    // objectFit: "cover"
                    }}
                  />

                  {!isMobile ? (
                    <Flex
                      justify="center"
                      align="center"
                      style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%"
                      }}
                    >
                      {!webcamOn ? (
                        <Title level={4} style={{ color: "white" }}>
                          {t("video.cameraIsOff")}
                        </Title>
                      ) : null}
                    </Flex>
                  ) : null}
                  <Flex
                    justify="center"
                    gap={16}
                    style={{
                      position: "absolute", bottom: 16, left: 0, width: "100%"
                    }}
                  >
                    {isMicrophonePermissionAllowed ? (
                      <Button
                        type="primary"
                        onClick={toggleMic}
                        shape="circle"
                        size="large"
                        danger={!micOn}
                        icon={micOn ? (
                          <AudioOutlined/>
                        ) : (
                          <AudioMutedOutlined/>
                        )}
                      />
                    ) : (
                      <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        danger
                        icon={<AudioMutedOutlined/>}
                      />
                    )}

                    {isCameraPermissionAllowed ? (
                      <Button
                        type="primary"
                        onClick={toggleWebcam}
                        shape="circle"
                        size="large"
                        danger={!webcamOn}
                        icon={webcamOn ? (
                          <VideoCameraOutlined/>
                        ) : (
                          <VideoCameraOutlined/>
                        )}
                      />
                    ) : (
                      <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        danger
                        icon={<VideoCameraOutlined/>}
                      />
                    )}
                  </Flex>
                </div>
                {!isMobile && (
                  <>
                    {/* <Affix style={{ position: "absolute", top: 10, right: 10 }}> */}
                    {/*  <NetworkStats/> */}
                    {/* </Affix> */}

                    <Row style={{ marginTop: 28 }} gutter={16}>
                      <Col span={8}>
                        <MicControls
                          customAudioStream={customAudioStream}
                          mics={mics}
                          changeMic={changeMic}
                          audioTrack={audioTrack}
                          micOn={micOn}
                          didDeviceChange={didDeviceChange}
                          setDidDeviceChange={setDidDeviceChange}
                        />
                      </Col>
                      <Col span={8}>
                        <CamControls
                          changeWebcam={changeWebcam}
                          webcams={webcams}
                        />
                      </Col>
                      <Col span={8}>
                        <SpeakersControls
                          speakers={speakers}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </Flex>
            </Col>
            <Col
              span={24}
              lg={8}
              style={{
                paddingTop: 16,
                paddingBottom: 16,
              }}
            >
              <MeetingDetailsScreen />
            </Col>
          </Row>
        </Content>
      </Layout>
      {contextHolder}
    </>
  );
};

export default JoiningScreen;
